import { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useParams, useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconDelete from '../../components/icons/IconDelete';
import WordSearchSelect from './WordSearchSelect';
import WordEditModal from './WordEditModal';
import IrregularVerbsBoard from '../../components/words/IrregularVerbsBoard';
import usePersistedState from '../../hooks/usePersistedState';

// red (0/unset) -> orange/yellow -> green, scaled linearly by hue - "the
// higher the proficiency, the greener" per the user's own description, rather
// than a few flat buckets.
const PROFICIENCY_MAX = 8;
const proficiencyColor = (p) => {
   const clamped = Math.max(0, Math.min(PROFICIENCY_MAX, p || 0));
   const hue = (clamped / PROFICIENCY_MAX) * 120;
   return 'hsl(' + hue + ', 70%, 50%)';
}

// "first meaning (+N)" - a word can now have several translations, this
// keeps table cells and search compact rather than dumping the whole list.
const translationSummary = (word) => {
   const translations = word.translations || [];
   if(translations.length === 0) return '';
   const first = translations[0].translation;
   return translations.length > 1 ? first + ' (+' + (translations.length - 1) + ')' : first;
}

const formatDate = (val) => {
   if(!val) {
      return '';
   }
   const dt = DateTime.fromISO(val);
   return dt.isValid ? dt.toFormat('dd.MM.yyyy HH:mm') : '';
}

const StudentWords = () => {
   const { id } = useParams();
   const navigate = useNavigate();
   const lang = language[language.lang].wordsTab;
   const langRoot = language[language.lang];
   const langIrregular = language[language.lang].irregularVerbsBoard;
   const [subTab, setSubTab] = usePersistedState('student-words-subtab', 'all');

   const [wordStudents, setWordStudents] = useState([]);
   const [status, setStatus] = useState('loading');
   const [refresh, setRefresh] = useState(0);
   const [search, setSearch] = useState('');
   const [posFilter, setPosFilter] = useState('');
   const [tenseFilter, setTenseFilter] = useState('');
   const [formFilter, setFormFilter] = useState('');
   const [sortMode, setSortMode] = useState('newest');
   const [partsOfSpeech, setPartsOfSpeech] = useState([]);
   const [tenses, setTenses] = useState([]);
   const [forms, setForms] = useState([]);
   const [pickedWordId, setPickedWordId] = useState('');
   const [adding, setAdding] = useState(false);
   const [editModalOpen, setEditModalOpen] = useState(false);
   const [editWordId, setEditWordId] = useState(null);
   const [wordSearchResetKey, setWordSearchResetKey] = useState(0);
   const [wordNoMatch, setWordNoMatch] = useState(false);
   const [wordNoMatchQuery, setWordNoMatchQuery] = useState('');
   const [quickCreating, setQuickCreating] = useState(false);

   const refreshList = () => setRefresh(prev => prev + 1);

   useEffect(() => {
      axios.get('/api/partsofspeech', { withCredentials: true }).then(r => setPartsOfSpeech(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/tenses', { withCredentials: true }).then(r => setTenses(Array.isArray(r.data) ? r.data : [])).catch(() => {});
      axios.get('/api/forms', { withCredentials: true }).then(r => setForms(Array.isArray(r.data) ? r.data : [])).catch(() => {});
   }, []);

   useEffect(() => {
      if(!id) {
         return;
      }
      setStatus('loading');
      axios.get('/api/wordstudents?student=' + id, { withCredentials: true })
         .then(response => {
            setWordStudents(Array.isArray(response.data) ? response.data : []);
            setStatus('done');
         })
         .catch(err => {
            if(err.response && err.response.status === 401) navigate('/login');
            setStatus('done');
         });
   }, [id, refresh, navigate]);

   const handleAddExisting = () => {
      if(!pickedWordId || adding) {
         return;
      }
      setAdding(true);
      axios.post('/api/wordstudents', JSON.stringify({ student: id, word: pickedWordId }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setPickedWordId('');
         setWordSearchResetKey(prev => prev + 1);
         refreshList();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setAdding(false));
   }

   // The search box's "+" button is dual-purpose: a match picked from the
   // dropdown -> handleAddExisting (above); nothing found for the typed text
   // -> here instead. Unlike the old separate "Додати нове слово" button
   // (which just opened a blank create form the teacher still had to Save),
   // this creates the word AND attaches it to the student immediately
   // (same atomic create-with-new-word endpoint WordEditModal itself uses),
   // then opens that same modal in EDIT mode on the freshly-created word so
   // AI-assist/manual details can be filled in right after - the word is
   // already real and in the student's list even if the modal gets closed
   // without touching anything else.
   const handleQuickCreate = () => {
      const text = wordNoMatchQuery.trim();
      if(!text || quickCreating) {
         return;
      }
      setQuickCreating(true);
      axios.post('/api/wordstudents/create-with-new-word', JSON.stringify({ student: id, word: { word: text } }), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then((response) => {
         setWordSearchResetKey(prev => prev + 1);
         setWordNoMatch(false);
         setWordNoMatchQuery('');
         refreshList();
         const newWordId = response.data && response.data.word && response.data.word.id;
         if(newWordId) {
            openEdit(newWordId);
         }
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setQuickCreating(false));
   }

   const handleDelete = (e, wordStudentId) => {
      e.stopPropagation();
      if(!window.confirm('Czy na pewno chcesz usunąć ten element?')) return;
      axios.delete('/api/wordstudents/' + wordStudentId, { withCredentials: true })
         .then(() => refreshList())
         .catch(err => console.log(err));
   }

   const openEdit = (wordId) => {
      setEditWordId(wordId);
      setEditModalOpen(true);
   }
   const closeEdit = () => setEditModalOpen(false);
   const onWordSaved = () => { closeEdit(); refreshList(); };

   const filtered = wordStudents.filter(ws => {
      if(!ws.word) return false;
      if(posFilter && (!ws.word.partOfSpeech || String(ws.word.partOfSpeech.id) !== String(posFilter))) return false;
      if(tenseFilter && (!ws.word.tense || String(ws.word.tense.id) !== String(tenseFilter))) return false;
      if(formFilter && (!ws.word.form || String(ws.word.form.id) !== String(formFilter))) return false;
      if(search.trim()) {
         const allTranslations = (ws.word.translations || []).map(t => t.translation).join(' ');
         const haystack = (ws.word.word + ' ' + allTranslations).toLowerCase();
         if(!haystack.includes(search.trim().toLowerCase())) return false;
      }
      return true;
   });

   let primary = filtered.filter(ws => ws.proficiency !== 0);
   const secondary = filtered.filter(ws => ws.proficiency === 0);
   if(sortMode === 'proficiency') {
      primary = [...primary].sort((a, b) => (b.proficiency || 0) - (a.proficiency || 0));
   }

   const renderRow = (ws) => (
      <tr key={ws.id} className='student-lessons-row' onClick={() => openEdit(ws.word.id)}>
         <td className='student-lessons-text-cell'>
            <span className='words-tab-word'>{ws.word.word}</span>
         </td>
         <td className='student-lessons-text-cell'>{translationSummary(ws.word)}</td>
         <td>{ws.word.partOfSpeech ? ws.word.partOfSpeech.description : ''}</td>
         <td>{ws.word.tense ? ws.word.tense.name : ''}</td>
         <td>{ws.word.form ? ws.word.form.name : ''}</td>
         <td>
            <span className='words-tab-proficiency-badge' style={{ borderColor: proficiencyColor(ws.proficiency), color: proficiencyColor(ws.proficiency) }}>
               {ws.proficiency}
            </span>
         </td>
         <td className='student-lessons-text-cell'>{formatDate(ws.lastSeenAt)}</td>
         <td className='student-lessons-actions-cell' onClick={e => e.stopPropagation()}>
            <div className='student-lessons-icon-btn' onClick={(e) => handleDelete(e, ws.id)}>
               <IconDelete style={{fill:'#e0876c', width:14, height:14, margin:0}} />
            </div>
         </td>
      </tr>
   );

   return (
      <div className='student-lessons'>
         <div className='student-card-tabs'>
            <div className={'student-card-tab' + (subTab === 'all' ? ' active' : '')} onClick={() => setSubTab('all')}>
               {lang.primaryWords}
            </div>
            <div className={'student-card-tab' + (subTab === 'irregular' ? ' active' : '')} onClick={() => setSubTab('irregular')}>
               {langIrregular.tabLabel}
            </div>
         </div>
         {subTab === 'irregular' ? <IrregularVerbsBoard studentId={id} /> : ''}
         {subTab === 'all' ? (
         <>
         <div className='student-lessons-toolbar'>
            <div className='words-tab-add-existing'>
               <WordSearchSelect key={wordSearchResetKey} value={pickedWordId} onChange={setPickedWordId} placeholder={lang.addExisting}
                  onNoMatchChange={(noMatch, query) => { setWordNoMatch(noMatch); setWordNoMatchQuery(query); }} />
               {wordNoMatch ? (
                  <div className={'student-lessons-add-btn lesson-word-quick-ai-btn' + (quickCreating ? ' conv-panel-add-btn-disabled' : '')}
                     onClick={handleQuickCreate} title={lang.addNew}>
                     <IconAdd /><span className='lesson-word-quick-ai-label'>AI</span>
                  </div>
               ) : (
                  <div className={'student-lessons-add-btn' + (!pickedWordId || adding ? ' conv-panel-add-btn-disabled' : '')}
                     onClick={handleAddExisting} title={lang.addExisting}>
                     <IconAdd />
                  </div>
               )}
            </div>
         </div>

         <FilterBar
            search={{ value: search, onChange: setSearch, placeholder: lang.searchPlaceholder }}
            filters={[
               {key:'pos', type:'select', label: lang.allPartsOfSpeech, options: partsOfSpeech.map(p => ({id:p.id, name:p.description})), value: posFilter, onChange: setPosFilter, numeric:true},
               {key:'tense', type:'select', label: lang.allTenses, options: tenses.map(t => ({id:t.id, name:t.name})), value: tenseFilter, onChange: setTenseFilter, numeric:true},
               {key:'form', type:'select', label: lang.allForms, options: forms.map(f => ({id:f.id, name:f.name})), value: formFilter, onChange: setFormFilter, numeric:true},
            ]}
            sort={{
               options: [{id:'newest', name: lang.sortNewest}, {id:'proficiency', name: lang.sortProficiency}],
               value: sortMode,
               onChange: setSortMode,
               label: lang.sortNewest,
            }}
            resultCount={filtered.length} />

         <p className='student-card-section-title words-tab-section-title'>{lang.primaryWords}</p>
         <div className='student-lessons-table-wrap'>
            <table className='student-lessons-table'>
               <thead>
                  <tr>
                     <th>{lang.word}</th>
                     <th>{lang.ukTranslation}</th>
                     <th>{lang.partOfSpeech}</th>
                     <th>{lang.tense}</th>
                     <th>{lang.form}</th>
                     <th>{lang.proficiency}</th>
                     <th></th>
                     <th></th>
                  </tr>
               </thead>
               <tbody>
                  {status === 'loading' ? (
                     <tr className='student-lessons-empty-row'><td colSpan={8}>{langRoot['Loading ...']}</td></tr>
                  ) : ''}
                  {status === 'done' ? primary.map(renderRow) : ''}
                  {status === 'done' && primary.length === 0 ? (
                     <tr className='student-lessons-empty-row'><td colSpan={8}>{lang.noWords}</td></tr>
                  ) : ''}
               </tbody>
            </table>
         </div>

         {secondary.length > 0 ? (
            <>
               <p className='student-card-section-title words-tab-section-title'>{lang.secondaryWords}</p>
               <div className='student-lessons-table-wrap'>
                  <table className='student-lessons-table'>
                     <tbody>
                        {secondary.map(renderRow)}
                     </tbody>
                  </table>
               </div>
            </>
         ) : ''}
         </>
         ) : ''}

         <WordEditModal isOpen={editModalOpen} wordId={editWordId} studentId={id}
            onClose={closeEdit} onSaved={onWordSaved} />
      </div>
   );
}

export default StudentWords;
